import React, { useState, useEffect, useRef } from 'react';
import { FiChevronRight, FiChevronLeft } from "react-icons/fi";
import { FaBook } from "react-icons/fa";
import styled from 'styled-components';
import videoData from '../../data/videoData.json';

// COLORES ARMÓNICOS
const COLOR_BG = "#02396d";
const COLOR_ACCENT = "#0151a2";
const COLOR_WHITE = "#fff";
const COLOR_SHADOW = "rgba(40,80,120,0.10)";

const SidebarContainer = styled.div`
  position: fixed;
  top: 4.5vw;
  left: 0;
  height: 90vh;
  width: ${(props) => (props.isCollapsed ? '80px' : '260px')};
  background: ${COLOR_BG};
  color: ${COLOR_WHITE};
  transition: width 0.3s;
  display: flex;
  flex-direction: column;
  border-right: none;
  z-index: 1000;
  overflow-y: auto;
  box-shadow: 0 8px 24px 0 ${COLOR_SHADOW};

  @media (max-width: 768px) {
    top: 10vh;
    height: calc(100vh - 10vh);
  }
`;

const SidebarToggle = styled.div`
  position: absolute;
  right: -16px;
  top: 18px;
  background: transparent;
  color: ${COLOR_WHITE};
  padding: 0;
  border-radius: 50%;
  cursor: pointer;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  z-index: 1010;
`;

const SidebarHeader = styled.div`
  text-align: center;
  padding: 20px 0 16px 0;
  border-bottom: 1px solid ${COLOR_ACCENT};
`;

const SidebarLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  h3 {
    color: ${COLOR_WHITE};
    font-weight: bold;
    font-size: 22px;
    margin: 0;
    letter-spacing: 1.2px;
  }
`;

const SidebarMenu = styled.nav`
  flex-grow: 1;
  padding: 10px 0;
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  a {
    display: flex;
    align-items: center;
    text-decoration: none;
    color: ${COLOR_WHITE};
    padding: 10px 18px;
    font-size: 16px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: background 0.2s, color 0.2s;
  }
`;

const MenuItem = styled.li`
  a {
    background: ${(props) => (props.active ? COLOR_ACCENT : 'transparent')};
    border-radius: 8px;
    font-weight: ${(props) => (props.active ? 'bold' : 'normal')};
    color: ${COLOR_WHITE};
    box-shadow: ${(props) => (props.active ? '0 2px 12px 0 rgba(1,81,162,0.10)' : 'none')};
    &:hover {
      background: ${COLOR_ACCENT};
      color: ${COLOR_WHITE};
    }
  }
`;

const Pagination = styled.div`
  text-align: center;
  padding: 10px;
  font-size: 14px;
  border-top: 1px solid ${COLOR_ACCENT};
  background: ${COLOR_BG};
  color: ${COLOR_WHITE};
  button {
    background: transparent;
    border: none;
    color: ${COLOR_WHITE};
    font-size: 16px;
    cursor: pointer;
    padding: 2px 8px;
    border-radius: 4px;
    &:hover:enabled {
      background: ${COLOR_ACCENT};
    }
    &:disabled {
      opacity: 0.4;
      cursor: default;
    }
  }
`;

const Sidebar = ({ onSelect }) => {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;
  const firstItemRef = useRef(null);

  const totalPages = Math.ceil(videoData.videos.length / itemsPerPage);
  const visibleVideos = videoData.videos.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    if (videoData.videos && videoData.videos.length > 0) {
      setSelectedVideo(videoData.videos[0]);
      onSelect(videoData.videos[0]);
    }
  }, [onSelect]);

  useEffect(() => {
    function checkMobile() {
      setIsCollapsed(window.innerWidth <= 768);
    }
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (firstItemRef.current) {
      firstItemRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentPage]);

  const handleToggle = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <SidebarContainer isCollapsed={isCollapsed}>
      <SidebarToggle onClick={handleToggle}>
        {isCollapsed ? <FiChevronRight size={24} /> : <FiChevronLeft size={24} />}
      </SidebarToggle>

      <SidebarHeader>
        <SidebarLogo>
          <FaBook size={26} color={COLOR_WHITE} />
          {!isCollapsed && <h3>CDB</h3>}
        </SidebarLogo>
      </SidebarHeader>

      <SidebarMenu>
        <ul>
          {visibleVideos.map((video, index) => (
            <MenuItem
              key={index}
              active={selectedVideo === video}
              ref={index === 0 ? firstItemRef : null}
            >
              <a
                href="#"
                onClick={() => {
                  setSelectedVideo(video);
                  onSelect(video);
                }}
                title={video.title}
              >
                {isCollapsed
                  ? index + 1 + (currentPage - 1) * itemsPerPage
                  : video.title}
              </a>
            </MenuItem>
          ))}
        </ul>
      </SidebarMenu>

      {totalPages > 1 && !isCollapsed && (
        <Pagination>
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            &lt;
          </button>
          Página {currentPage} de {totalPages}
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            &gt;
          </button>
        </Pagination>
      )}
    </SidebarContainer>
  );
};

export default Sidebar;
